import {
  FilledTextFieldProps,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { MuiOtpInput, MuiOtpInputProps } from 'mui-one-time-password-input';
import { useEffect } from 'react';
import {
  Controller,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form';

import OptionalLabel from 'components/@forms/OptionalLabel';

interface FormOTPFieldProps {
  codeLength?: number;
}

type Props = FormOTPFieldProps &
  MuiOtpInputProps &
  Pick<UseControllerProps, 'rules' | 'name'> &
  Pick<FilledTextFieldProps, 'label' | 'helperText' | 'autoFocus' | 'type'>;

const CODE_LENGTH = 6;

const FormOTPField = ({
  rules,
  name,
  label,
  helperText,
  codeLength = CODE_LENGTH,
  type = 'number',
  ...rest
}: Props) => {
  const { control } = useFormContext();

  useEffect(() => {
    if (rest.autoFocus) {
      // Focus on OTP input when the field is focused
      // This is not automatically handled by mui-one-time-password-input
      const inputElements = document.getElementsByTagName('input');
      const array = [...inputElements];
      array.find((el) => el.autocomplete === 'one-time-code')?.focus();
    }
  }, [rest.autoFocus]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        return (
          <Stack
            sx={{
              gap: 0.5,
            }}
          >
            {!!label && (
              <Typography variant="small">
                <OptionalLabel label={label} required={rules?.required} />
              </Typography>
            )}
            <MuiOtpInput
              length={codeLength}
              gap={1}
              maxWidth="450px"
              TextFieldsProps={{
                type,
                inputProps: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                },
                variant: 'filled',
                // Styling added to remove arrows from input https://stackoverflow.com/questions/3790935/can-i-hide-the-html5-number-input-s-spin-box
                sx: {
                  borderRadius: 1,
                  '& input': {
                    padding: '16px 8px',
                    MozAppearance: 'textfield',
                  },
                  '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
                    {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                },
                autoComplete: 'one-time-code',
              }}
              {...field}
              {...rest}
            />
            {fieldState.invalid ? (
              <FormHelperText error>{fieldState.error?.message}</FormHelperText>
            ) : null}
          </Stack>
        );
      }}
    />
  );
};

export default FormOTPField;
